import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Trash2 } from "lucide-react";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { formatDisplayDate } from "../../../helpers/date";
import { notificationLink } from "../notification.helpers";
import { useNotifications, useMarkNotificationAsRead, useRemoveNotification } from "../hooks";
import { NotificationsQuery_notifications_results } from "../../../__generated__/NotificationsQuery";

interface IProps {
  /** N'afficher que les non-lues (cloche du header) */
  onlyUnread?: boolean;
  /** Limiter le nombre d'éléments affichés */
  max?: number;
  /** Appelé après un clic sur une notification (ex. fermer le panneau) */
  onNavigate?: () => void;
}

export const NotificationsList: React.FC<IProps> = ({
  onlyUnread = false,
  max,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const { data, loading } = useNotifications({
    where: onlyUnread ? { isRead: false } : {},
  });
  const [markAsRead] = useMarkNotificationAsRead();
  const [removeNotification] = useRemoveNotification();

  if (loading) return <Loading />;

  const results = data?.notifications?.results ?? [];
  const notifications = max ? results.slice(0, max) : results;

  if (!notifications.length) {
    return <EmptyList text={onlyUnread ? "Aucune notification non lue" : "Aucune notification"} />;
  }

  const handleClick = async (notification: NotificationsQuery_notifications_results) => {
    if (!notification.isRead) {
      try {
        await markAsRead({ variables: { id: notification.id } });
      } catch (error: any) {
        toast.error(error?.message ?? "Une erreur est survenue");
      }
    }

    const link = notificationLink(notification.table, notification.objectId);
    if (link) {
      onNavigate?.();
      navigate(link);
    }
  };

  const handleRemove = async (
    event: React.MouseEvent,
    id: number
  ) => {
    event.stopPropagation();
    try {
      const { data } = await removeNotification({ variables: { id } });
      if (data?.removeNotification?.ok) {
        toast.success("Notification supprimée");
      } else {
        toast.error(data?.removeNotification?.error ?? "Une erreur est survenue");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Une erreur est survenue");
    }
  };

  return (
    <div className="divide-y divide-gray-200 dark:divide-zinc-800">
      {notifications.map((notification) => {
        const hasLink = !!notificationLink(notification.table, notification.objectId);
        return (
          <div
            key={`notification-${notification.id}`}
            onClick={() => handleClick(notification)}
            className={`flex items-start gap-3 px-3 py-3 transition-colors ${
              hasLink ? "cursor-pointer" : ""
            } ${
              notification.isRead
                ? "hover:bg-gray-50 dark:hover:bg-zinc-900"
                : "bg-primary-50 dark:bg-zinc-800 hover:bg-primary-100 dark:hover:bg-zinc-700"
            }`}
          >
            {!notification.isRead && (
              <span className="mt-1.5 w-2 h-2 rounded-full bg-primary-600 dark:bg-zinc-300 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm truncate ${
                  notification.isRead
                    ? "text-gray-700 dark:text-zinc-300"
                    : "font-semibold text-gray-900 dark:text-zinc-100"
                }`}
              >
                {notification.title}
              </p>
              {notification.content && (
                <p className="text-xs text-gray-500 dark:text-zinc-400 mt-0.5">
                  {notification.content}
                </p>
              )}
              <p className="text-xs text-gray-400 dark:text-zinc-500 mt-0.5">
                {formatDisplayDate(notification.createdAt)}
              </p>
            </div>
            <button
              type="button"
              onClick={(event) => handleRemove(event, notification.id)}
              title="Supprimer"
              className="text-gray-400 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0"
            >
              <Trash2 size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
};
